import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { trackButtonClick } from '../analytics';
import { getPercentage } from '../utils/resultSummary';
import { getDaysUntilExam } from '../utils/examCountdown';
import { calculateStudyStreak, getWeekStudyDays } from '../utils/studyStreak';
import { formatDuration } from '../components/StudyPlanCalendar/StudyPlanCalendar';
import Onboarding from './Onboarding/Onboarding';
import UpgradeCTA from '../components/UpgradeCTA';
import PlansModal from '../components/PlansModal';
import PlanTabButton from '../components/PlanTabButton';
import LogoutButton from '../components/LogoutButton';
import ThemeToggle from '../components/ThemeToggle';
import PageLoader from '../components/PageLoader';
import DashboardTopbar from '../components/DashboardTopbar';
import SubjectPerformancePanel from '../components/SubjectPerformancePanel/SubjectPerformancePanel';
import StudyTimePanel from '../components/StudyTimePanel/StudyTimePanel';
import EditalProgressPanel from '../components/EditalProgressPanel/EditalProgressPanel';
import TodayStudyPanel from '../components/TodayStudyPanel/TodayStudyPanel';
import PhysicalTrainingPanel from '../practice/PhysicalTraining/PhysicalTrainingPanel';
import RankingPanel from '../components/RankingPanel/RankingPanel';
import { useCourse } from '../contexts/CourseContext';
import {
  MenuBookOutlined,
  AutoStoriesOutlined,
  CalendarMonthOutlined,
  PlayCircleOutline,
  LockOutlined,
  HomeOutlined,
  AssignmentOutlined,
  InsightsOutlined,
  ArticleOutlined,
  FileDownloadOutlined,
  ReportProblemOutlined,
  CheckOutlined,
  EditNoteOutlined,
  FitnessCenterOutlined,
  EmojiEventsOutlined
} from '../icons';
import './MyMocksPage.css';

const getInitials = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Funil guiado (10 questões teste) desativado por enquanto — mantido no
// código (comentado onde referenciado), não usar sem reavaliar antes.
const TRIAL_FLOW_STORAGE_KEY = 'trialFlowCompleted';

// Desativado temporariamente: a prova está muito próxima, então não faz sentido pedir
// pro aluno montar um cronograma semanal agora. O fluxo de plano de estudos guiado
// (StudyPlan.js em modo "guided", com a intro animada) continua intacto no código —
// só troque para `true` quando isso voltar a fazer sentido (próximo concurso/ciclo)
// para o funil guiado voltar a passar por lá antes das 10 questões.
const GUIDED_FLOW_STUDY_PLAN_STEP_ENABLED = false;

// Meta de taxa de acerto usada só para colorir o card "Taxa de acerto" em
// Meu desempenho (acima = card esverdeado, abaixo = card em alerta).
const ACERTO_GOAL_PERCENT = 60;

const Simulados = () => {
  const navigate = useNavigate();
  const { activeCourseId, courses, activeCourse, loadingCourses } = useCourse();
  const isCustomCourse = !!activeCourse?.isCustom;
  // onboarding_completed agora vem do backend (por usuário, não mais por
  // navegador) — só sabemos se mostra depois que o /dashboard responder.
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [mocks, setMocks] = useState([])
  const [loading, setLoading] = useState(true);
  const [hasFreePlan, setHasFreePlan] = useState(false);
  const [studentName, setStudentName] = useState('Aluno');
  const [activeTab, setActiveTab] = useState('home');
  const [userId, setUserId] = useState();
  const [statistics, setStatistics] = useState(null);
  const [studyPlan, setStudyPlan] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [selectedMockStatId, setSelectedMockStatId] = useState('');
  const [studySessions, setStudySessions] = useState([]);
  const [editalProgress, setEditalProgress] = useState(null);
  const [showMockPaywall, setShowMockPaywall] = useState(false);
  const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/?$/, '/');

  useEffect(() => {
    // Espera o CourseContext resolver o concurso ativo a partir do banco
    // (GET /studyPlan/activeCourse) antes de decidir pra onde redirecionar —
    // senão redirecionaria pra tela de escolha de concurso mesmo pra quem já
    // tem um concurso com plano, só porque activeCourseId ainda está vazio
    // nesse primeiro instante (a preferência ainda está sendo validada).
    if (loadingCourses) return;

    setLoading(true);
    axios.get(`${apiUrl}dashboard`, { params: { courseId: activeCourseId || undefined } })
      .then(({ data }) => {
        const hasFreePlan = data.hasFreePlan;
        const plan = data.studyPlan?.plan || null;
        // Usado só pelo funil guiado comentado abaixo.
        // const guidedOnboarding = !!data.userInformation?.guided_onboarding;

        setMocks(data.mocks);
        setHasFreePlan(hasFreePlan);
        setStudentName(data.name || data.userInformation?.name || 'Aluno');
        setUserId(data.userInformation?.id);
        setStatistics(data.statistics);
        setStudyPlan(plan);
        setMaterials(data.materials || []);
        setStudySessions(data.studySessions || []);
        setEditalProgress(data.editalProgress || null);
        setShowOnboarding(!data.userInformation?.onboarding_completed);

        // Funil guiado (10 questões teste) desativado por enquanto — não
        // apagar, só comentado. Reativar revertendo este bloco quando fizer
        // sentido de novo (ver comentário de GUIDED_FLOW_STUDY_PLAN_STEP_ENABLED).
        // if (
        //   guidedOnboarding &&
        //   hasFreePlan &&
        //   !plan &&
        //   !localStorage.getItem(TRIAL_FLOW_STORAGE_KEY)
        // ) {
        //   if (GUIDED_FLOW_STUDY_PLAN_STEP_ENABLED) {
        //     navigate('/plano-de-estudos', { replace: true, state: { guided: true } });
        //   } else {
        //     navigate('/questao-treino', { replace: true });
        //   }
        // } else if (!activeCourseId) {
        if (!activeCourseId) {
          navigate('/escolher-concurso', { replace: true });
        }
        // Se o concurso ativo não tem plano ainda (ex.: usuário trocou pra um
        // concurso novo pelo CourseSwitcher), NÃO navega pra
        // /selecionar-materias automaticamente — isso usava `replace: true` e
        // apagava /simulados do histórico, deixando o botão Voltar do
        // navegador preso num loop entre login e seleção de matérias. O
        // dashboard já tem o próprio empty state (painel de aderência /
        // sidebar "Plano de estudos") pra levar o usuário lá quando ele
        // quiser, com navegação normal (push) que preserva o histórico.
      })
      .catch(error => {
        console.error('Erro ao buscar os dados da página:', error);
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, activeCourseId, loadingCourses]);

  useEffect(() => {
    if (isCustomCourse && (activeTab === 'simulados' || activeTab === 'ranking')) {
      setActiveTab('home');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomCourse, activeTab]);

  useEffect(() => {
    if (!activeCourse?.hasPhysicalTraining && activeTab === 'treino-fisico') {
      setActiveTab('home');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCourse, activeTab]);

  const tapOnCourse = (id) => {
    navigate('/questoes', { state: { mock: { id: id } } });
  };

  const makeMock = (mock) => {
    if (mock.hits == null) {
      if (mock.accessible === false) {
        setShowMockPaywall(true);
      } else {
        navigate('/questoes', { state: { mock: mock } })
      }
    } else {
      navigate('/success', { state: { mock: { id: mock.id, name: mock.name } } });
    }
  };

  const renderMockCard = (mock, index) => {
    const isLocked = mock.hits == null && mock.accessible === false;
    const isDone = mock.hits != null;
    return (
      <div className={`mock-card${isLocked ? ' mock-card--locked' : ''}`} key={index}>
        <div className="mock-card-info">
          <span className="mock-card-name">{mock.name}</span>
          <span className={`mock-status-badge ${isDone ? 'done' : isLocked ? 'locked' : 'available'}`}>
            {isDone ? (
              `${mock.hits} acertos`
            ) : isLocked ? (
              <>
                <LockOutlined style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />
                Bloqueado
              </>
            ) : (
              'Disponível'
            )}
          </span>
        </div>
        <div className="mock-card-action">
          <button onClick={() => makeMock(mock)}>
            {isDone ? 'Visualizar Resultado' : isLocked ? 'Acesso completo' : 'Realizar Simulado'}
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="App">
        <ThemeToggle />
        <LogoutButton />
        <PageLoader message="Carregando seu perfil..." />
      </div>
    );
  }

  const goToPractice = () => {
    navigate('/questao');
  };

  const closeOnboarding = () => {
    setShowOnboarding(false);
    axios.put(`${apiUrl}user/onboarding`).catch((error) => {
      console.error('Erro ao salvar onboarding:', error);
    });
  };

  const statsOverview = statistics?.overview || { totalRespondidas: 0, acertos: 0, erros: 0, percentual: 0, simuladosAnalisados: 0 };
  const statsBySubject = statistics?.porMateria || [];
  const statsByMock = statistics?.porSimulado || [];
  const getStatPercentage = (correct, total) => getPercentage(correct, total);
  const getProgressClass = (percentage) => {
    if (percentage <= 30) return 'low';
    if (percentage <= 69) return 'medium';
    return 'high';
  };

  const totalStudyMinutes = studySessions.reduce((sum, session) => sum + (session.duration_minutes || 0), 0);
  const studyTimeBySubject = (() => {
    const bySubject = new Map();
    studySessions.forEach((s) => {
      const minutes = s.duration_minutes || 0;
      if (minutes <= 0) return;
      const materia = s.subject_name || 'Sem matéria';
      const subj = bySubject.get(materia) || { materia, minutos: 0, topicosMap: new Map() };
      subj.minutos += minutes;
      if (s.topic_name) {
        const t = subj.topicosMap.get(s.topic_name) || { topico: s.topic_name, minutos: 0 };
        t.minutos += minutes;
        subj.topicosMap.set(s.topic_name, t);
      }
      bySubject.set(materia, subj);
    });
    return Array.from(bySubject.values())
      .map((s) => ({
        materia: s.materia,
        minutos: s.minutos,
        topicos: Array.from(s.topicosMap.values()).sort((a, b) => b.minutos - a.minutos)
      }))
      .sort((a, b) => b.minutos - a.minutos);
  })();
  const studyStreak = calculateStudyStreak(studySessions);
  const weekStudyDays = getWeekStudyDays(studySessions);
  const studiedToday = weekStudyDays.find((day) => day.isToday)?.studied ?? false;
  const daysUntilExam = getDaysUntilExam(activeCourse?.exam_date);
  const questoesRespondidasFormatted = statsOverview.totalRespondidas.toLocaleString('pt-BR');
  const activeCourseName = courses.find((c) => String(c.id) === String(activeCourseId))?.name;
  const selectedMockStat =
    statsByMock.find((item) => String(item.mockId) === String(selectedMockStatId)) || statsByMock[0];
  // Em concurso personalizado sem simulados nem progresso de edital ainda
  // (comum logo após criar o concurso), a coluna principal fica vazia e o
  // grid de 2 colunas (1fr 320px) sobra só com a sidebar, que aí cai na
  // primeira coluna (1fr) em vez da estreita — vira uma lista larga e feia.
  // Nesse caso o dashboard-main muda pra 1 coluna e a sidebar vira um grid
  // de cards (ver .dashboard-main--sidebar-only no CSS).
  const hasEditalProgress = !!(editalProgress?.overview && editalProgress.overview.totalTopicos > 0);
  const showMainColumn = !isCustomCourse || hasEditalProgress;

  return (
    <div className="App dashboard-page">
      <LogoutButton />
      {showOnboarding && <Onboarding onClose={closeOnboarding} />}
      {showMockPaywall && (
        <PlansModal
          trackingPrefix="trial_wall_simulado"
          heroTitle="Você já usou seu simulado gratuito"
          heroSubtitle="Libere acesso completo para continuar praticando com todos os simulados até a prova da Polícia Penal RS."
          onClose={() => setShowMockPaywall(false)}
        />
      )}

      <DashboardTopbar
        allowCourseChange
        right={(
          <>
            <button
              type="button"
              className="dashboard-icon-button"
              onClick={() => setShowOnboarding(true)}
              aria-label="Ver funcionalidades"
              title="Ver funcionalidades"
            >
              <PlayCircleOutline style={{ fontSize: 18 }} />
            </button>
            <ThemeToggle />
            <div
              className="dashboard-avatar"
              title={studentName}
              role="button"
              tabIndex={0}
              onClick={() => navigate('/perfil')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') navigate('/perfil');
              }}
              aria-label="Ver perfil do aluno"
            >
              {getInitials(studentName)}
            </div>
          </>
        )}
      />

      <div className="dashboard-content-card">
      <div className="tabs-buttons">
        <button
          className={`tab-button ${activeTab === 'home' ? 'active' : ''}`}
          onClick={() => setActiveTab('home')}
        >
          <HomeOutlined style={{ fontSize: 18, verticalAlign: 'middle', marginRight: 6 }} />
          Home
        </button>
        {!isCustomCourse && (
          <button
            className={`tab-button ${activeTab === 'simulados' ? 'active' : ''}`}
            onClick={() => setActiveTab('simulados')}
          >
            <AssignmentOutlined style={{ fontSize: 18, verticalAlign: 'middle', marginRight: 6 }} />
            Simulados
          </button>
        )}
        <button
          className={`tab-button ${activeTab === 'estatisticas' ? 'active' : ''}`}
          onClick={() => setActiveTab('estatisticas')}
        >
          <InsightsOutlined style={{ fontSize: 18, verticalAlign: 'middle', marginRight: 6 }} />
          Meu desempenho
        </button>
        <button
          className={`tab-button ${activeTab === 'material' ? 'active' : ''}`}
          onClick={() => setActiveTab('material')}
        >
          <AutoStoriesOutlined style={{ fontSize: 18, verticalAlign: 'middle', marginRight: 6 }} />
          Material para estudos
        </button>
        {!!activeCourse?.hasPhysicalTraining && (
          <button
            className={`tab-button ${activeTab === 'treino-fisico' ? 'active' : ''}`}
            onClick={() => setActiveTab('treino-fisico')}
          >
            <FitnessCenterOutlined style={{ fontSize: 18, verticalAlign: 'middle', marginRight: 6 }} />
            Treino físico
          </button>
        )}
        {!isCustomCourse && (
          <button
            className={`tab-button ${activeTab === 'ranking' ? 'active' : ''}`}
            onClick={() => setActiveTab('ranking')}
          >
            <EmojiEventsOutlined style={{ fontSize: 18, verticalAlign: 'middle', marginRight: 6 }} />
            Ranking
          </button>
        )}
        <PlanTabButton hasFreePlan={hasFreePlan} />
      </div>

      {hasFreePlan && (
        <UpgradeCTA
          variant="banner"
          trackingPrefix="dashboard_banner"
          title="Seu estudo está no caminho certo. Acelere com acesso completo."
          subtitle="Acesse todo o conteúdo da nossa plataforma."
          showWhatsapp={false}
          benefits={[]}
        />
      )}

      <a className="whatsapp-btn" href="https://wa.me/555596507899?text=Olá%2C%20gostaria%20de%20mais%20informações" target="_blank" rel="noopener noreferrer">
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.28-1.39c1.44.79 3.06 1.2 4.71 1.2h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.876 9.876 0 0012.05 2h-.01zm5.8 14.16c-.24.68-1.4 1.3-1.93 1.38-.49.08-1.11.11-1.79-.11-.41-.13-.94-.3-1.62-.6-2.85-1.23-4.71-4.1-4.85-4.29-.14-.19-1.16-1.54-1.16-2.94s.73-2.09.99-2.37c.26-.29.56-.36.75-.36.19 0 .38 0 .54.01.17.01.41-.07.64.49.24.58.81 2 .88 2.15.07.15.12.32.02.51-.09.19-.14.31-.28.48-.14.17-.29.37-.42.5-.14.14-.28.29-.12.57.16.28.71 1.17 1.52 1.9 1.05.94 1.93 1.23 2.21 1.37.28.14.44.12.6-.07.16-.19.68-.79.87-1.07.19-.28.37-.23.62-.14.26.09 1.63.77 1.91.91.28.14.47.21.54.33.07.12.07.68-.17 1.36z" />
        </svg>
        <span>Fale conosco</span>
      </a>

      {activeTab === 'home' && (
        <div className="tab-content">
          <div className="kpi-strip">
            <div className="kpi-card kpi-card--primary">
              <span className="kpi-label">APROVEITAMENTO GERAL</span>
              <span className="kpi-value">{statsOverview.percentual}%</span>
              <span className="kpi-sub">Baseado em {questoesRespondidasFormatted} questões</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">QUESTÕES RESPONDIDAS</span>
              <span className="kpi-value">{questoesRespondidasFormatted}</span>
              <span className="kpi-sub">{statsOverview.simuladosAnalisados} simulados analisados</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">SEQUÊNCIA DE ESTUDO</span>
              <span className="kpi-value">{studyStreak} {studyStreak === 1 ? 'dia' : 'dias'}</span>
              <span className="kpi-sub">{studyStreak > 0 ? 'continue assim!' : 'estude hoje para começar'}</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">TOTAL ESTUDADO</span>
              <span className="kpi-value kpi-value--small">{totalStudyMinutes > 0 ? formatDuration(totalStudyMinutes) : '0min'}</span>
              <span className="kpi-sub">registrado no diário de estudos</span>
            </div>
            <div className="kpi-card kpi-card--exam">
              <span className="kpi-label">DATA DA PROVA</span>
              {daysUntilExam != null ? (
                <>
                  <span className="kpi-value kpi-value--small">{daysUntilExam} {daysUntilExam === 1 ? 'dia' : 'dias'}</span>
                  <span className="kpi-sub">{activeCourseName || 'Seu concurso'}</span>
                </>
              ) : (
                <>
                  <span className="kpi-value kpi-value--small">—</span>
                  <span className="kpi-sub">{activeCourse?.exam_date ? 'Prova já realizada' : 'Data não definida'}</span>
                </>
              )}
            </div>
          </div>

          <TodayStudyPanel plan={studyPlan} />

          <div className={`dashboard-main${showMainColumn ? '' : ' dashboard-main--sidebar-only'}`}>
            {!isCustomCourse && (
              <div className="dashboard-main-list">
                <div className="edital-progress-header">
                  <span className="edital-progress-icon">
                    <AssignmentOutlined style={{ fontSize: 20 }} />
                  </span>
                  <div>
                    <h3>Simulados</h3>
                    <span className="edital-progress-subtitle">
                      Comece um novo simulado.
                    </span>
                  </div>
                </div>

                <div className="mock-list">
                  {mocks.slice(0, 2).map((mock, index) => renderMockCard(mock, index))}
                </div>
                {mocks.length > 2 && (
                  <button type="button" className="see-all-mocks-link" onClick={() => setActiveTab('simulados')}>
                    Ver todos os simulados →
                  </button>
                )}

                {editalProgress && (
                  <EditalProgressPanel overview={editalProgress.overview} data={editalProgress.porMateria} />
                )}
              </div>
            )}

            {isCustomCourse && editalProgress && (
              <EditalProgressPanel overview={editalProgress.overview} data={editalProgress.porMateria} />
            )}

            <aside className="quick-access-sidebar">
              <div className="streak-card">
                <div className="streak-card-header">
                  <span className="streak-card-title">Sua sequência</span>
                  <span className="streak-card-badge">🔥 {studyStreak} {studyStreak === 1 ? 'dia' : 'dias'}</span>
                </div>
                <div className="streak-week">
                  {weekStudyDays.map((day) => (
                    <div key={day.key} className="streak-day">
                      <span className={`streak-day-label${day.isToday ? ' streak-day-label--today' : ''}`}>{day.label}</span>
                      <span
                        className={`streak-day-dot${day.studied ? ' streak-day-dot--studied' : ''}${!day.studied && day.isToday ? ' streak-day-dot--today' : ''}`}
                      >
                        {day.studied ? (
                          <CheckOutlined style={{ fontSize: 14 }} />
                        ) : (
                          day.isToday && <span className="streak-day-dot-mark" />
                        )}
                      </span>
                    </div>
                  ))}
                </div>
                <span className="streak-caption">
                  {studiedToday
                    ? 'Você já estudou hoje. Sequência garantida!'
                    : 'Estude hoje para manter sua sequência viva.'}
                </span>
              </div>

              <span className="quick-access-sidebar-label">Acesso rápido</span>
              <button type="button" className="sidebar-card sidebar-card--accent" onClick={() => navigate('/diario-de-estudos')}>
                <EditNoteOutlined style={{ fontSize: 24 }} />
                <strong>Diário de estudos</strong>
                <small>Registre questões e revisões</small>
              </button>
              <button type="button" className="sidebar-card" onClick={goToPractice}>
                <MenuBookOutlined style={{ fontSize: 22 }} />
                <strong>Realizar questões</strong>
                <small>Questões organizadas por tema</small>
              </button>
              <button type="button" className="sidebar-card" onClick={() => navigate('/plano-de-estudos')}>
                <CalendarMonthOutlined style={{ fontSize: 22 }} />
                <strong>Plano de estudos</strong>
                <small>Monte sua semana por prioridade</small>
              </button>
              <button type="button" className="sidebar-card" onClick={() => navigate('/meus-erros')}>
                <ReportProblemOutlined style={{ fontSize: 22 }} />
                <strong>Meus erros</strong>
                <small>Revise com explicação</small>
              </button>
              {!!activeCourse?.hasPhysicalTraining && (
                <button type="button" className="sidebar-card" onClick={() => navigate('/treino-fisico')}>
                  <FitnessCenterOutlined style={{ fontSize: 22 }} />
                  <strong>Treino físico</strong>
                  <small>Registre seus exercícios do TAF</small>
                </button>
              )}
            </aside>
          </div>
        </div>
      )}

      {activeTab === 'simulados' && (
        <div className="tab-content">
          {mocks.length > 0 ? (
            <div className="mock-list">
              {mocks.map((mock, index) => renderMockCard(mock, index))}
            </div>
          ) : (
            <div className="empty-state">Em breve teremos simulados disponíveis para o seu concurso.</div>
          )}
        </div>
      )}

      {activeTab === 'material' && (
        <div className="tab-content">
          <div className="dark-panel-card">
            <h2>Material de Estudo</h2>
            <p className="material-description">
              Baixe nossas apostilas em PDF para aprofundar seus estudos. Total de <strong>{materials.length}</strong> apostilas disponíveis.
            </p>
            <div className="apostilas-grid">
              {materials.map((material) => {
                const canDownload = material.is_free || !hasFreePlan;
                const isNew = material.is_new;

                return (
                  <div key={material.id} className={`apostila-card${isNew ? ' apostila-card--highlight' : ''}`}>
                    {isNew && <span className="apostila-badge-new">Novidade</span>}
                    <div className="apostila-icon">
                      <ArticleOutlined style={{ fontSize: 40 }} />
                    </div>
                    <h3>{material.nome}</h3>

                    <a
                      href={
                        canDownload
                          ? material.url
                          : "https://pay.cakto.com.br/poemijy"
                      }
                      download={canDownload}
                      className={`download-button${canDownload ? '' : ' locked'}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackButtonClick((canDownload ? "button_clicked_baixar_" : "button_clicked_apostilas_comprar_") + userId)}
                    >
                      {canDownload ? (
                        <>
                          <FileDownloadOutlined style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4 }} />
                          Download PDF
                        </>
                      ) : (
                        <>
                          <LockOutlined style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4 }} />
                          Acesso completo
                        </>
                      )}
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'treino-fisico' && (
        <div className="tab-content">
          <PhysicalTrainingPanel />
        </div>
      )}

      {activeTab === 'ranking' && (
        <div className="tab-content">
          <RankingPanel courseId={activeCourseId} studentName={studentName} />
        </div>
      )}

      {activeTab === 'estatisticas' && (
        <div className="tab-content">
          <div className="stats-header">
            <h2 className="stats-title">Estatística do seu desempenho</h2>
            <span className="stats-subtitle">
              Veja seu rendimento por matéria e por simulado com base nas respostas registradas.
            </span>
          </div>

          <div className="kpi-strip stats-kpi-strip">
            <div className="kpi-card kpi-card--primary">
              <span className="kpi-label">QUESTÕES RESPONDIDAS</span>
              <span className="kpi-value">{questoesRespondidasFormatted}</span>
            </div>
            <div className={`kpi-card kpi-card--acerto ${statsOverview.percentual >= ACERTO_GOAL_PERCENT ? 'kpi-card--good' : 'kpi-card--exam'}`}>
              <div className="kpi-card-top">
                <span className="kpi-label">TAXA DE ACERTO</span>
                <span className="kpi-goal-badge">
                  {statsOverview.percentual >= ACERTO_GOAL_PERCENT ? '+' : '-'}
                  {Math.abs(statsOverview.percentual - ACERTO_GOAL_PERCENT)} pts da meta
                </span>
              </div>
              <div className="kpi-acerto-row">
                <span className="kpi-value">{statsOverview.percentual}%</span>
                <div className="kpi-acerto-track">
                  <div
                    className="kpi-acerto-fill"
                    style={{ width: `${Math.min(statsOverview.percentual, 100)}%` }}
                  />
                  <div className="kpi-acerto-goal" style={{ left: `${Math.min(ACERTO_GOAL_PERCENT, 100)}%` }} />
                </div>
              </div>
              <span className="kpi-sub">meta de {ACERTO_GOAL_PERCENT}% marcada na régua</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">SIMULADOS ANALISADOS</span>
              <span className="kpi-value">{statsOverview.simuladosAnalisados}</span>
              {statsOverview.simuladosAnalisados === 0 && (
                <span className="kpi-sub">termine um simulado para ver a análise aqui</span>
              )}
            </div>
          </div>

          <div className="stats-panels stats-panels--grid">
            <SubjectPerformancePanel data={statsBySubject} />

            <div className="stats-panels-column">
              <div className={`stats-panel${statsByMock.length > 0 ? '' : ' stats-panel--cta'}`}>
                <h3>Desempenho por simulado</h3>
                {statsByMock.length > 0 ? (
                  <>
                    <div className="mock-select-wrap">
                      <select
                        className="mock-select"
                        value={selectedMockStat?.mockId ?? ''}
                        onChange={(e) => setSelectedMockStatId(e.target.value)}
                      >
                        {statsByMock.map((item) => (
                          <option key={item.mockId} value={item.mockId}>{item.nome}</option>
                        ))}
                      </select>
                      <span className="mock-select-arrow">▾</span>
                    </div>
                    {selectedMockStat && (
                      <div className="stat-item stat-item--featured">
                        <div className="stat-header">
                          <span>{selectedMockStat.nome}</span>
                          <strong>{getStatPercentage(selectedMockStat.acertos, selectedMockStat.total)}%</strong>
                        </div>
                        <div className="stat-bar-track">
                          <div
                            className={`stat-bar-fill ${getProgressClass(getStatPercentage(selectedMockStat.acertos, selectedMockStat.total))}`}
                            style={{ width: `${Math.min(getStatPercentage(selectedMockStat.acertos, selectedMockStat.total), 100)}%` }}
                          />
                        </div>
                        <small>{selectedMockStat.acertos} de {selectedMockStat.total} questões corretas</small>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <span className="stats-panel-cta-text">
                      Você ainda não concluiu simulados para analisar. Finalize um para ver o comparativo aqui.
                    </span>
                    <button type="button" className="stats-panel-cta-link" onClick={() => setActiveTab('simulados')}>
                      Ver simulados disponíveis →
                    </button>
                  </>
                )}
              </div>

              <StudyTimePanel data={studyTimeBySubject} totalMinutes={totalStudyMinutes} />
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Simulados;
